import React, { useEffect } from "react";

import NarratorIntonation from "../intonation/NarratorIntonation";
import IntonationFeedbackDisplay from "../intonation/IntonationFeedbackDisplay";
import IntonationHelpModal from "../intonation/IntonationHelpModal";

const GraphHelpButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="ml-auto inline-flex min-h-8 shrink-0 items-center gap-1.5 rounded-full border border-blue-200/80 bg-blue-50/55 px-2.5 py-1 text-[11px] font-semibold text-blue-700 transition-[background-color,border-color,transform] hover:border-blue-300 hover:bg-blue-50 active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/65 focus-visible:ring-offset-2 sm:min-h-9 sm:px-3 sm:py-1.5 sm:text-xs"
    aria-label="How to read the pitch and intensity graph"
  >
    <span
      className="flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] ring-1 ring-blue-200"
      aria-hidden="true"
    >
      i
    </span>
    <span className="whitespace-nowrap">How to read this graph</span>
  </button>
);

const PitchGraphGuide = () => (
  <div className="hidden flex-wrap items-center gap-x-4 gap-y-1 border-b border-slate-200/70 bg-slate-50/75 px-4 py-2 text-xs sm:flex">
    <span className="inline-flex items-center gap-1.5 font-semibold text-blue-700">
      <span
        className="block w-5 rounded-full border-t-2 border-blue-600"
        aria-hidden="true"
      />
      Pitch
    </span>

    <span className="inline-flex items-center gap-1.5 font-semibold text-slate-700">
      <span
        className="block w-5 rounded-full border-t-2 border-slate-500"
        aria-hidden="true"
      />
      Intensity
    </span>

    <span className="sm:ml-auto text-[11px] font-medium text-slate-500">
      Blue gaps usually mark voiceless sounds or pauses.
    </span>
  </div>
);

const IntonationComparisonPanel = ({
  showIntonationVisualization,
  showIntonationHelp,
  setShowIntonationHelp,
  segment,
  assessment,
  subject,
  file,
  handleLaneWordClick,
  broadcastTime,
  audioref,
  visualizationView,
  rate,
  setRate,
  auth,
  resolvedAssessmentAudioUrl,
  resolvedNarratorAssessmentAudioUrl,
  fitViewport = false,
  compactViewport = false,
  userFirst = false,
  showRateMe = false,
  onRateMe,
}) => {
  const showUserFirst = Boolean(userFirst && assessment);
  const isPitchView = visualizationView === "pitch";

  useEffect(() => {
    if (!isPitchView && showIntonationHelp) {
      setShowIntonationHelp(false);
    }
  }, [isPitchView, showIntonationHelp, setShowIntonationHelp]);

  if (!showIntonationVisualization) return null;

  return (
    <section
      className={[
        "w-full overflow-hidden rounded-[24px] border bg-white ring-1 ring-slate-950/[0.02] transition-[border-color,box-shadow,background-color] duration-300",
        showUserFirst
          ? "border-slate-200/80 bg-white shadow-[0_18px_46px_-34px_rgba(15,23,42,0.32)]"
          : "border-slate-200/90 bg-white shadow-[0_18px_46px_-34px_rgba(15,23,42,0.28)]",
        fitViewport
          ? "flex min-h-0 flex-1 flex-col"
          : "",
      ].join(" ")}
    >
      {showUserFirst && showRateMe && (
        <button
          type="button"
          onClick={onRateMe}
          className="group flex w-full shrink-0 items-center justify-between gap-3 border-b border-purple-700 bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-2 text-left text-white shadow-md shadow-purple-500/15 transition-all hover:from-purple-700 hover:to-indigo-700 hover:shadow-lg hover:shadow-purple-500/20 active:scale-[0.998] sm:px-5"
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/20 text-sm ring-1 ring-white/25"
              aria-hidden="true"
            >
              👥
            </span>

            <div className="truncate text-sm font-bold sm:text-base">
              Ask the community
            </div>
          </div>

          <span className="inline-flex min-h-9 shrink-0 items-center gap-2 rounded-lg bg-white px-3.5 py-1.5 text-sm font-bold text-purple-700 shadow-sm transition-transform group-hover:translate-x-0.5">
            Request feedback
            <span aria-hidden="true">→</span>
          </span>
        </button>
      )}

      <div
        className={[
          "flex flex-col",
          fitViewport
            ? "min-h-0 flex-1"
            : "",
        ].join(" ")}
      >
        {segment?.words?.length > 0 && (
          <div
            className={[
              showUserFirst
                ? "order-2 border-t border-slate-100 bg-slate-50/35"
                : "order-1",
              fitViewport
                ? "flex min-h-0 flex-1 flex-col"
                : "",
              compactViewport
                ? "p-2"
                : showUserFirst
                ? "p-3 sm:p-4"
                : "p-3 sm:p-4",
            ].join(" ")}
          >
            <div
              className={[
                "flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1.5",
                compactViewport ? "mb-1.5" : "mb-2.5",
              ].join(" ")}
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-[11px] text-white shadow-sm ring-1 ring-white/70 sm:h-8 sm:w-8 sm:rounded-xl sm:text-xs">
                🎭
              </div>

              <h4 className="text-[15px] font-semibold tracking-[-0.01em] text-slate-800 sm:text-base">
                Narrator
              </h4>

              {!showUserFirst && isPitchView && (
                <GraphHelpButton
                  onClick={() => setShowIntonationHelp(true)}
                />
              )}
            </div>

            <div
              className={[
                "overflow-hidden rounded-[20px] border border-slate-200/90 bg-white shadow-[0_14px_34px_-30px_rgba(15,23,42,0.34)]",
                fitViewport
                  ? "flex min-h-[164px] flex-1 flex-col sm:min-h-[220px]"
                  : "min-h-[220px] sm:min-h-[240px]",
              ].join(" ")}
            >
              {!showUserFirst && isPitchView && (
                <PitchGraphGuide />
              )}

              <div
                className={[
                  fitViewport
                    ? "flex min-h-0 flex-1"
                    : "",
                  compactViewport
                    ? "p-1 sm:p-2"
                    : "p-2 sm:p-3",
                ].join(" ")}
              >
                <NarratorIntonation
                  subject={subject}
                  file={file}
                  segment={segment}
                  onWordClick={handleLaneWordClick}
                  broadcastTime={broadcastTime}
                  audioRef={audioref}
                  view={visualizationView}
                  speed={rate}
                  fitViewport={fitViewport}
                />
              </div>

              {!fitViewport && (
                <div className="hidden border-t border-slate-200/70 bg-slate-50/65 px-3 py-2.5 sm:block sm:px-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-xs font-medium text-slate-500 sm:text-sm">
                      Tap a word to replay it.
                    </div>

                    <div className="flex items-center justify-between gap-3 sm:justify-end">
                      <span className="text-xs font-semibold text-slate-500">
                        Speed
                      </span>

                      <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
                        {[0.25, 0.5, 1].map((speed) => {
                          const active = rate === speed;
                          const label = `${Math.round(speed * 100)}%`;

                          return (
                            <button
                              key={speed}
                              type="button"
                              aria-pressed={active}
                              aria-label={`Set playback speed to ${label}`}
                              onClick={() => {
                                setRate(speed);
                                if (audioref.current) {
                                  audioref.current.playbackRate = speed;
                                }
                              }}
                              className={[
                                "min-w-[52px] rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-[background-color,color,box-shadow,transform] sm:text-sm",
                                active
                                  ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-sm"
                                  : "text-slate-600 hover:bg-slate-50 active:translate-y-px",
                              ].join(" ")}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {assessment && (
          <div
            className={[
              showUserFirst
                ? "order-1 bg-white p-3 sm:p-4"
                : "order-2 border-t border-white/25 p-4 sm:p-5",
            ].join(" ")}
          >
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-xs text-white shadow-sm">
                🎙️
              </div>

              <h4 className="font-semibold text-gray-800">
                Your recording
              </h4>

              {showUserFirst && isPitchView && (
                <GraphHelpButton
                  onClick={() => setShowIntonationHelp(true)}
                />
              )}

              {!showUserFirst && (
                <span className="ml-auto hidden text-[11px] text-gray-500 md:inline">
                  Compare with the narrator
                </span>
              )}
            </div>

            <div
              className={[
                "min-h-[200px] overflow-hidden rounded-xl sm:min-h-[220px]",
                showUserFirst
                  ? "border border-slate-200/80 bg-white shadow-sm"
                  : "border border-white/50 bg-white/60",
              ].join(" ")}
            >
              {showUserFirst && isPitchView && (
                <PitchGraphGuide />
              )}

              <div className="p-1.5 sm:p-2">
                <IntonationFeedbackDisplay
                  assessment={assessment}
                  segment={segment}
                  view={visualizationView}
                  isAdmin={auth?.role === "ADMIN"}
                  audioUrl={resolvedAssessmentAudioUrl}
                  narratorAudioUrl={resolvedNarratorAssessmentAudioUrl}
                />
              </div>
            </div>
          </div>
        )}

        {!assessment && !fitViewport && (
          <div className="order-2 border-t border-white/25 p-4 sm:p-5">
            <div className="rounded-xl border border-dashed border-gray-300/70 bg-white/50 p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/70 border border-white/50 flex items-center justify-center">
                🎧
              </div>

              <div>
                <div className="font-semibold text-gray-800">
                  Record to see your curve here
                </div>

                <p className="text-sm text-gray-600">
                  After recording, you'll be able to replay your audio and compare
                  it with the narrator above.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <IntonationHelpModal
        open={isPitchView && showIntonationHelp}
        onClose={() => setShowIntonationHelp(false)}
      />
    </section>
  );
};

export default IntonationComparisonPanel;